import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ListingStatusBadge } from './ListingStatusBadge';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Trash, ArrowCounterClockwise, MagnifyingGlass, Funnel, CheckCircle, XCircle, Warning } from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import { getCategoryName, getSubcategoryName } from '../utils/categoryNames';
import { useTranslation } from 'react-i18next';
import { AdminPagination } from './admin/AdminPagination';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { key: 'all', label: 'All Active', color: '' },
  { key: 'active', label: 'Active', color: 'bg-success-soft text-success' },
  { key: 'expired', label: 'Expired', color: 'bg-warning-soft text-warning' },
  { key: 'rejected', label: 'Rejected', color: 'bg-danger-soft text-danger' },
];

export const ListingsManagement = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('active');
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [subcategoryFilter, setSubcategoryFilter] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [titleSearch, setTitleSearch] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const v = Number(localStorage.getItem('admin.listings.pageSize'));
    return [10, 25, 50].includes(v) ? v : 10;
  });
  const [pagination, setPagination] = useState({ total: 0, page: 1, page_size: 10, total_pages: 0 });

  useEffect(() => { localStorage.setItem('admin.listings.pageSize', String(pageSize)); }, [pageSize]);

  // Bulk selection
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    axios.get(`${API}/admin/structure/categories`, { withCredentials: true })
      .then(res => setCategories(res.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (categoryFilter) {
      const cat = categories.find(c => c.key === categoryFilter);
      setSubcategories(cat?.subcategories || []);
    } else {
      setSubcategories([]);
    }
    setSubcategoryFilter('');
  }, [categoryFilter, categories]);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const params = new URLSearchParams();
      params.set('tab', tab);
      if (tab !== 'trash' && statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (categoryFilter) params.set('category', categoryFilter);
      if (subcategoryFilter) params.set('subcategory', subcategoryFilter);
      if (userSearch) params.set('user_search', userSearch);
      if (titleSearch) params.set('search', titleSearch);
      params.set('page', String(page));
      params.set('page_size', String(pageSize));
      const { data } = await axios.get(`${API}/admin/listings?${params}`, { withCredentials: true });
      const items = Array.isArray(data) ? data : (data?.items || []);
      setListings(items);
      if (!Array.isArray(data) && data) {
        setPagination({
          total: data.total || 0,
          page: data.page || 1,
          page_size: data.page_size || pageSize,
          total_pages: data.total_pages || 0,
        });
      }
    } catch { toast.error('Failed to load listings'); }
    finally { setLoading(false); }
  }, [tab, statusFilter, categoryFilter, subcategoryFilter, userSearch, titleSearch, page, pageSize]);

  // Reset to page 1 whenever a filter or the tab changes.
  useEffect(() => { setPage(1); }, [tab, statusFilter, categoryFilter, subcategoryFilter, userSearch, titleSearch]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const handleModerate = async (id, status) => {
    try {
      await axios.put(`${API}/admin/listings/${id}/moderate?status=${status}`, {}, { withCredentials: true });
      toast.success(`Listing ${status}`);
      fetchListings();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleSoftDelete = async (id) => {
    try {
      await axios.put(`${API}/admin/listings/${id}/soft-delete`, {}, { withCredentials: true });
      toast.success('Moved to trash');
      fetchListings();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleRestore = async (id) => {
    try {
      await axios.put(`${API}/admin/listings/${id}/restore`, {}, { withCredentials: true });
      toast.success('Listing restored');
      fetchListings();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handlePermanentDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/listings/${id}/permanent`, { withCredentials: true });
      toast.success('Permanently deleted');
      fetchListings();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleBulkAction = async (action) => {
    if (selected.size === 0) return;
    try {
      await axios.post(`${API}/admin/listings/bulk-action?action=${action}`, Array.from(selected), { withCredentials: true });
      toast.success(`${selected.size} listings updated`);
      setSelected(new Set());
      fetchListings();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === listings.length) setSelected(new Set());
    else setSelected(new Set(listings.map(l => l.listing_id)));
  };

  const isTrash = tab === 'trash';

  return (
    <div data-testid="listings-management">
      {/* Tabs */}
      <div className="flex items-center gap-3 mb-5">
        <h2 className="text-xl font-bold">Listings</h2>
        <div className="flex gap-1 ml-auto">
          <Button size="sm" variant={!isTrash ? 'default' : 'outline'} onClick={() => setTab('active')} data-testid="tab-listings" className={!isTrash ? 'bg-primary text-white' : ''}>
            Published
          </Button>
          <Button size="sm" variant={isTrash ? 'default' : 'outline'} onClick={() => setTab('trash')} data-testid="tab-trash" className={isTrash ? 'bg-primary text-white' : ''}>
            <Trash size={14} className="mr-1" /> Trash
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4 p-3 bg-surface rounded-lg border border-border" data-testid="listing-filters">
          <div className="flex items-center gap-1 flex-1 min-w-[200px]">
            <MagnifyingGlass size={16} className="text-text_muted" />
            <Input placeholder="Search title..." value={titleSearch} onChange={(e) => setTitleSearch(e.target.value)} className="h-8 text-xs border-border" data-testid="filter-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-[120px] text-xs" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={categoryFilter || 'all'} onValueChange={(v) => setCategoryFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {subcategories.length > 0 && (
            <Select value={subcategoryFilter || 'all'} onValueChange={(v) => setSubcategoryFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="filter-subcategory"><SelectValue placeholder="Subcategory" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subcategories</SelectItem>
                {subcategories.map(s => <SelectItem key={s.value} value={s.value}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Input placeholder="User name/email..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="h-8 w-[160px] text-xs border-border" data-testid="filter-user" />
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={fetchListings} data-testid="apply-filters">
            <Funnel size={14} className="mr-1" /> Filter
          </Button>
        </div>

      {/* Bulk Actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-2 mb-3 p-2 bg-primary/5 rounded-lg border border-primary/20" data-testid="bulk-actions">
          <span className="text-sm font-medium text-primary">{selected.size} selected</span>
          <Button size="sm" variant="success" onClick={() => handleBulkAction('active')} className="h-7 text-xs" data-testid="bulk-accept"><CheckCircle size={14} className="mr-1" /> Accept</Button>
          <Button size="sm" variant="outline" onClick={() => handleBulkAction('rejected')} className="h-7 text-xs text-danger border-danger" data-testid="bulk-reject"><XCircle size={14} className="mr-1" /> Reject</Button>
          <Button size="sm" variant="outline" onClick={() => handleBulkAction('deleted')} className="h-7 text-xs text-text_muted" data-testid="bulk-delete"><Trash size={14} className="mr-1" /> Delete</Button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface border-b border-border">
              <tr>
                {!isTrash && (
                  <th className="p-3 w-10">
                    <Checkbox checked={listings.length > 0 && selected.size === listings.length} onCheckedChange={toggleSelectAll} />
                  </th>
                )}
                <th className="text-left p-3 font-semibold text-xs">Title</th>
                <th className="text-left p-3 font-semibold text-xs">Seller</th>
                <th className="text-left p-3 font-semibold text-xs">Status</th>
                {isTrash && <th className="text-left p-3 font-semibold text-xs">Deleted</th>}
                <th className="text-left p-3 font-semibold text-xs">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr><td colSpan={isTrash ? 5 : 5} className="p-8 text-center text-text_muted">Loading...</td></tr>
              ) : listings.length === 0 ? (
                <tr><td colSpan={isTrash ? 5 : 5} className="p-8 text-center text-text_muted">{isTrash ? 'Trash is empty' : 'No listings found'}</td></tr>
              ) : listings.map((l) => {
                return (
                  <tr key={l.listing_id} className={`${selected.has(l.listing_id) ? 'bg-primary/5' : ''}`} data-testid={`admin-listing-${l.listing_id}`}>
                    {!isTrash && (
                      <td className="p-3"><Checkbox checked={selected.has(l.listing_id)} onCheckedChange={() => toggleSelect(l.listing_id)} /></td>
                    )}
                    <td className="p-3">
                      <Link to={`/listings/${l.listing_id}`} className="font-medium hover:text-primary text-sm">{l.title}</Link>
                      <p className="text-xs text-text_muted">{getCategoryName(l.category, t)} {l.subcategory && `· ${getSubcategoryName(l.subcategory, t)}`} · {l.city}</p>
                    </td>
                    <td className="p-3 text-sm">{l.user_name}<br/><span className="text-xs text-text_muted">{l.user_email}</span></td>
                    <td className="p-3"><ListingStatusBadge status={l.status} showActive /></td>
                    {isTrash && (
                      <td className="p-3 text-xs text-text_muted">{l.deleted_at ? formatDistanceToNow(new Date(l.deleted_at), { addSuffix: true }) : ''}</td>
                    )}
                    <td className="p-3">
                      <div className="flex gap-1.5">
                        {isTrash ? (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleRestore(l.listing_id)} className="h-7 px-2 text-xs text-success border-success hover:bg-success-soft" data-testid={`restore-${l.listing_id}`}>
                              <ArrowCounterClockwise size={14} className="mr-1" /> Restore
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="destructive" className="h-7 px-2 text-xs" data-testid={`perm-delete-${l.listing_id}`}>
                                  <Warning size={14} className="mr-1" /> Delete Forever
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Permanently Delete?</AlertDialogTitle>
                                  <AlertDialogDescription>This will permanently remove "{l.title}" and all associated data. This action cannot be undone.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handlePermanentDelete(l.listing_id)} className="bg-danger hover:bg-danger">Delete Forever</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </>
                        ) : (
                          <>
                            {l.status !== 'active' && <Button size="sm" onClick={() => handleModerate(l.listing_id, 'active')} className="bg-success hover:bg-success text-white h-7 px-2 text-xs" data-testid={`approve-${l.listing_id}`}>Accept</Button>}
                            {l.status !== 'rejected' && <Button size="sm" variant="outline" onClick={() => handleModerate(l.listing_id, 'rejected')} className="h-7 px-2 text-xs text-danger border-danger" data-testid={`reject-${l.listing_id}`}>Reject</Button>}
                            <Button size="sm" variant="ghost" onClick={() => handleSoftDelete(l.listing_id)} className="h-7 px-2 text-xs text-text_muted hover:text-danger" data-testid={`delete-${l.listing_id}`}>
                              <Trash size={14} />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <AdminPagination
        total={pagination?.total ?? 0}
        page={pagination?.page ?? page}
        pageSize={pagination?.page_size ?? pageSize}
        totalPages={pagination?.total_pages ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(v) => { setPage(1); setPageSize(v); }}
        testIdPrefix="listing"
      />
    </div>
  );
};
